import React from "react";
import { Link } from "react-router-dom";
import {
  Terminal, Mail, Check, ChevronLeft, ShieldCheck, FileText, ArrowRight, Inbox, Ban,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.8 — Public Email Policy / Consent page.
// Backs up our claim to email service providers (Resend, SendGrid, AWS SES,
// etc.) that consent is explicit, logged, and easy to withdraw.

export default function Consent() {
  usePageview("/consent");

  return (
    <div className="min-h-screen bg-black text-white" data-testid="consent-page">
      {/* Top nav */}
      <header className="border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link
            to="/security"
            className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2"
          >
            TRUST CENTER →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // EMAIL POLICY · CONSENT · OPT-IN AUDIT TRAIL
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tight leading-[1.05]">
            How DARA gets, verifies, and respects email consent.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-5 leading-relaxed">
            We only send email to people who have explicitly opted in via a
            check-box on a DARA form. Every opt-in is timestamped, IP-logged,
            and one-click reversible. Below is exactly how we do it, where it
            happens, and what we send.
          </p>
        </div>
      </section>

      {/* 1) WHERE CONSENT IS CAPTURED */}
      <section className="py-14 border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-3">
            // 1 · WHERE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-6">
            Every email collection has a required, explicit opt-in box.
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            We collect email addresses in only three places. Each one shows a
            required checkbox <em>before</em> the user can submit. No
            pre-checked boxes, no hidden defaults, no "if you don't reply
            we'll keep emailing".
          </p>

          <div className="space-y-3">
            <ConsentSourceRow
              testid="src-signup"
              url="/signup"
              label="Workspace sign-up"
              copy='"I agree to receive DARA briefings, alerts, and product emails."'
              note="Required to create an account. Stores email_consent_at + email_consent_ip on the user record."
            />
            <ConsentSourceRow
              testid="src-founders-guide"
              url="/founders-guide"
              label="Founder's Guide download"
              copy={`"I agree to receive the Founder's Guide + one short follow-up email two weeks later."`}
              note="Required to receive the PDF. Stores email_consent_at + email_consent_ip on the lead record."
            />
            <ConsentSourceRow
              testid="src-industrial"
              url="/industrial"
              label="Industrial Pilot waitlist"
              copy="Waitlist signup. Email used to schedule pilot kick-off only. No marketing emails sent without further opt-in."
              note="Stored under industrial_waitlist with explicit pilot-application flag."
            />
          </div>

          <div className="mt-6 font-mono text-[10px] text-zinc-600 leading-relaxed">
            Verify any of the above forms — open the URL, you'll see the checkbox before the submit button.
          </div>
        </div>
      </section>

      {/* 2) WHAT WE SEND */}
      <section className="py-14 border-b border-zinc-900 bg-zinc-950/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-3">
            // 2 · WHAT
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-6">
            Three kinds of email — all transactional or opted-in.
          </h2>

          <div className="space-y-3">
            <EmailType
              icon={ShieldCheck}
              label="TRANSACTIONAL"
              examples="Login confirmation · password reset · billing receipts · invitations you sent"
              note="Sent to all account holders — required for service operation. CAN-SPAM exempt."
            />
            <EmailType
              icon={Inbox}
              label="WORKSPACE BRIEFINGS"
              examples="Daily DARA Briefing · weekly Sales Pulse · activation digest"
              note="Sent ONLY to accounts whose owner ticked the consent box at signup. Owner can disable at Settings → Notifications."
            />
            <EmailType
              icon={Mail}
              label="MARKETING / PRODUCT"
              examples="New-feature announcements · case studies · ~4 emails per quarter, max"
              note="Sent ONLY to verified opt-in addresses. Unsubscribe header + footer link in every send."
            />
          </div>

          <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
            We do <strong className="text-white">not</strong> buy email lists. We do{" "}
            <strong className="text-white">not</strong> scrape public sources. We do{" "}
            <strong className="text-white">not</strong> append addresses from data
            brokers. Every recipient is a person who typed their email into
            one of our forms and ticked a box.
          </p>
        </div>
      </section>

      {/* 3) HOW WE VERIFY */}
      <section className="py-14 border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-3">
            // 3 · HOW WE VERIFY
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-6">
            Audit trail + bounce monitoring keeps the list clean.
          </h2>

          <ul className="space-y-3">
            <Item>
              <strong className="text-white">Pydantic-enforced server-side check</strong> —
              the <code className="font-mono text-[11px] bg-zinc-950 px-1.5 py-0.5 border border-zinc-800 text-[#FFB000]">email_consent</code> field is required at the API boundary. Bypassing the
              UI returns a 400 error.
            </Item>
            <Item>
              <strong className="text-white">Timestamp + IP logged</strong> on every
              opt-in (<code className="font-mono text-[11px] bg-zinc-950 px-1.5 py-0.5 border border-zinc-800 text-[#FFB000]">email_consent_at</code>,{" "}
              <code className="font-mono text-[11px] bg-zinc-950 px-1.5 py-0.5 border border-zinc-800 text-[#FFB000]">email_consent_ip</code>).
              Available on request for any individual record.
            </Item>
            <Item>
              <strong className="text-white">Resend bounce webhook</strong> auto-removes
              hard-bounced addresses from future sends. Soft bounces tracked
              and suppressed after 3 consecutive fails.
            </Item>
            <Item>
              <strong className="text-white">One-click unsubscribe</strong> in every
              non-transactional email. Honored within 60 seconds (writes to{" "}
              <code className="font-mono text-[11px] bg-zinc-950 px-1.5 py-0.5 border border-zinc-800 text-[#FFB000]">email_unsubscribed_at</code>),
              filtering all future sends globally for that address.
            </Item>
            <Item>
              <strong className="text-white">14-day re-confirmation</strong> for any
              opt-in older than 24 months on quiet accounts.
            </Item>
          </ul>
        </div>
      </section>

      {/* 4) UNSUBSCRIBE */}
      <section className="py-14 bg-zinc-950/30 border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-3">
            // 4 · UNSUBSCRIBE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-4">
            Three ways out, no questions asked.
          </h2>
          <div className="space-y-3 mt-5">
            <Item>
              Click the <strong className="text-white">unsubscribe</strong> link in any DARA email — instant, no login required.
            </Item>
            <Item>
              From the app: <strong className="text-white">Settings → Notifications</strong> → toggle individual email categories off.
            </Item>
            <Item>
              Email <a href="mailto:unsubscribe@daraos.ai" className="text-[#FFB000] hover:underline">unsubscribe@daraos.ai</a> from the address you'd like removed.
            </Item>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
            Questions, complaints, or audit requests?
          </h2>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed max-w-xl mx-auto">
            We respond within one business day. Auditors and ESP compliance
            teams can request the consent log for any address by emailing
            our security team.
          </p>
          <div className="flex items-center justify-center gap-3 mt-7 flex-wrap">
            <a
              href="mailto:security@daraos.ai?subject=DARA%20-%20Email%20consent%20audit%20request"
              data-testid="consent-mail-security"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              <Mail className="w-3.5 h-3.5" />
              SECURITY@DARAOS.AI
            </a>
            <Link
              to="/security"
              className="border border-zinc-800 hover:border-zinc-700 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              <FileText className="w-3.5 h-3.5" />
              TRUST CENTER
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="mt-10">
            <Link
              to="/"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1"
            >
              <ChevronLeft className="w-3 h-3" /> BACK TO HOMEPAGE
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ConsentSourceRow({ testid, url, label, copy, note }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-900 bg-zinc-950/40 p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base">{label}</div>
          <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
            daraos.ai{url}
          </div>
        </div>
        <Link
          to={url}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testid}-link`}
          className="border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
        >
          VISIT
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="mt-3 border-l-2 border-[#FFB000]/40 pl-3 text-sm text-zinc-300 italic">
        {copy}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 mt-3 leading-relaxed">
        {note}
      </div>
    </div>
  );
}

function EmailType({ icon: Icon, label, examples, note }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-4 flex items-start gap-3">
      <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
        <Icon className="w-4 h-4 text-[#FFB000]" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">
          // {label}
        </div>
        <div className="font-heading font-bold text-base mt-0.5">{examples}</div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
          {note}
        </div>
      </div>
    </div>
  );
}

function Item({ children }) {
  return (
    <li className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
      <Check className="w-3.5 h-3.5 text-[#00E676] flex-shrink-0 mt-1" />
      <span>{children}</span>
    </li>
  );
}
